import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MemberAvatar } from '@/components/MemberAvatar';
import { Camera, Loader2, Lock, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * Admin / Super-Admin dialog to edit a member's profile.
 *
 * Permission rules (mirrored from the backend so the UI never shows
 * disabled-but-bypassable fields):
 *   • Super Admin → may edit anyone except cannot demote another Super Admin.
 *   • Admin → may edit only Members and Viewers (and cannot promote to Admin).
 *
 * Submits a single ``PUT /api/members/{id}`` for profile/password and
 * (optionally) ``POST /api/members/{id}/avatar`` if a new avatar was picked.
 */
export function EditMemberDialog({ open, onOpenChange, member, currentUser, onSaved }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [password, setPassword] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Re-seed form whenever a different member is opened
  useEffect(() => {
    if (member) {
      setName(member.name || '');
      setEmail(member.email || '');
      setRole(member.role || 'member');
      setPhone(member.phone || '');
      setCompany(member.company || '');
      setPassword('');
      setAvatarFile(null);
      setAvatarPreview(null);
    }
  }, [member]);

  if (!member) return null;

  const isSuperAdmin = currentUser?.role === 'super_admin';
  const targetIsSuperAdmin = member.role === 'super_admin';
  const canChangeRole = !targetIsSuperAdmin;
  const canPromoteToAdmin = isSuperAdmin && !targetIsSuperAdmin;

  const handlePickAvatar = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(f.type)) {
      toast.error('Pick a JPEG, PNG, WebP, or GIF image');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setAvatarFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target?.result);
    reader.readAsDataURL(f);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (!email.trim() || !email.includes('@')) { toast.error('Valid email is required'); return; }
    if (password && password.length < 6) { toast.error('Password must be at least 6 characters'); return; }

    setSaving(true);
    try {
      const body = { name: name.trim(), email: email.trim(), phone, company };
      if (canChangeRole && role && role !== member.role) body.role = role;
      if (password.trim()) body.password = password.trim();
      await api.updateMemberProfile(member.id, body);

      if (avatarFile) {
        await api.uploadMemberAvatar(member.id, avatarFile);
      }

      toast.success('Member updated');
      onSaved?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update member');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-member-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg font-bold text-slate-900">Edit Member</DialogTitle>
        </DialogHeader>

        {/* Avatar uploader */}
        <div className="flex items-center gap-4 py-2">
          <div className="relative">
            {avatarPreview ? (
              <img
                src={avatarPreview}
                alt="Avatar preview"
                className="w-16 h-16 rounded-full object-cover border-2 border-slate-200"
                data-testid="avatar-preview"
              />
            ) : (
              <MemberAvatar userId={member.id} name={member.name} size="lg" />
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#0047AB] hover:bg-[#003580] text-white flex items-center justify-center shadow-md transition-colors"
              aria-label="Change avatar"
              data-testid="change-avatar-btn"
            >
              <Camera className="w-3.5 h-3.5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handlePickAvatar}
              className="hidden"
              data-testid="avatar-file-input"
            />
          </div>
          <div className="text-xs text-slate-500">
            <p className="font-medium text-slate-700">Profile picture</p>
            <p>JPG, PNG, WebP, GIF · max 5 MB</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} className="mt-1 rounded-sm" data-testid="edit-member-name" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Email</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} className="mt-1 rounded-sm" data-testid="edit-member-email" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Phone</Label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} className="mt-1 rounded-sm" data-testid="edit-member-phone" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Company</Label>
            <Input value={company} onChange={e => setCompany(e.target.value)} className="mt-1 rounded-sm" data-testid="edit-member-company" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Role</Label>
            {canChangeRole ? (
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-member-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {canPromoteToAdmin && <SelectItem value="admin">Admin</SelectItem>}
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Input value="Super Admin" disabled className="mt-1 rounded-sm bg-slate-50" />
            )}
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">New Password</Label>
            <div className="relative">
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Leave blank to keep current"
                className="mt-1 rounded-sm pl-8"
                autoComplete="new-password"
                data-testid="edit-member-password"
              />
              <Lock className="absolute left-2 top-1/2 -translate-y-1/2 mt-0.5 w-3.5 h-3.5 text-slate-400" />
            </div>
          </div>
        </div>

        <p className="text-[10px] text-slate-400 mt-1">
          Use the <strong>Permissions</strong> menu to toggle module access for this member.
        </p>

        <DialogFooter className="mt-4 flex items-center justify-between gap-2 sm:flex-row">
          {(() => {
            // Mirror MembersPage canImpersonate so Login-As-User only appears
            // when the caller has authority.
            const isSA = currentUser?.role === 'super_admin';
            const sameUser = currentUser?.id === member.id;
            const targetSA = member.role === 'super_admin';
            const canImp = !sameUser && !targetSA && (isSA || (currentUser?.role === 'admin' && (member.role === 'member' || member.role === 'viewer')));
            if (!canImp) return <div />;
            return (
              <Button
                type="button"
                variant="ghost"
                onClick={async () => {
                  if (!window.confirm(`Log in as ${member.name}? You'll see exactly what they see; click "Return to admin" in the top banner to come back.`)) return;
                  try {
                    await api.impersonateMember(member.id);
                    toast.success(`Now signed in as ${member.name}`);
                    window.location.assign('/dashboard');
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Could not impersonate user');
                  }
                }}
                className="text-amber-700 hover:text-amber-900 hover:bg-amber-50 mr-auto"
                data-testid="edit-member-impersonate"
              >
                <LogIn className="w-4 h-4 mr-1.5" />
                Log in as {member.name.split(' ')[0]}
              </Button>
            );
          })()}
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving} data-testid="edit-member-cancel">Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0047AB] hover:bg-[#003580] text-white"
              data-testid="edit-member-save"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : 'Save changes'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
